import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from "@ngx-translate/core";
import {BreakpointObserver} from "@angular/cdk/layout";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {filter, firstValueFrom, map, Subscription} from "rxjs";
import {FormService} from "./api";
import {AuthService} from "@auth0/auth0-angular";
import {Roles, UserInfoService} from "./services/user-info-service/user-info.service";
import { environment } from 'src/assets/env/environment';
import { AppVersionService, VersionInfo } from './services/version-service/version.service';
import { KnowledgePanelService } from './services/knowledge-service/knowledge-panel.service';
import { BrandingService } from './services/branding-service/branding.service';
import { AuditRealtimeService } from './services/audit-realtime-service/audit-realtime.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Docuty Backoffice';
  isLoginPage = false;
  hideSidebar = false;
  isBackofficeUser = true;
  isMobile = window.innerWidth < 768;
  loading = true;

  /**
   * DOCU-751: Menüpunkt "Export-Vorlagen" vorerst ausgeblendet — im aktuellen
   * Zustand nicht rollout-reif (u.a. fehlt die Plan-/Kundenabhängigkeit).
   * Route und Seite bleiben erhalten. Reaktivierung: dieses Flag flippen bzw.
   * später durch die Per-Org-Freischaltung ersetzen (DOCU-752,
   * Settings.Features.ExportTemplates).
   */
  exportTemplatesMenuEnabled = false;

  private authSub?: Subscription;

  public currentUrl = "";

  versionInfo?: VersionInfo;

  @HostListener('window:resize')
  onResize() {
    this.isMobile = window.innerWidth < 768;
  }

  constructor(
    private translate: TranslateService,
    private breakpointObserver: BreakpointObserver,
    public authService: AuthService,
    private router: Router,
    private service: FormService,
    protected userInfoService: UserInfoService,
    private route: ActivatedRoute,
    private versionService: AppVersionService,
    private brandingService: BrandingService,
    public knowledgePanelService: KnowledgePanelService,
    private auditRealtime: AuditRealtimeService
  ) {
  }

  get logoUrl(): string {
    return this.brandingService.logoUrl;
  }

  get logoDarkUrl(): string {
    return this.brandingService.logoDarkUrl;
  }

  /** Fallback auf gebundeltes Logo wenn externes Branding-Logo nicht erreichbar. */
  onLogoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.onerror = null; // Loop verhindern
    img.src = '/assets/img/logo_white.webp';
  }

  isSmallScreen = false;

  async ngOnInit() {
    this.translate.addLangs(['de', 'en']);
    this.translate.setDefaultLang('de');
    const browserLang = this.translate.getBrowserLang();
    const lang = browserLang == undefined ? 'de' : browserLang.match(/de|en/) ? browserLang : 'de';
    this.translate.use(lang);

    this.versionService.getVersionInfo().subscribe(v => {
      this.versionInfo = v;
    });

    this.isLoginPage = this.computeIsLoginPage(this.router.url);
    this.hideSidebar = this.isRegistrationPage(this.router.url);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        //@ts-ignore
        this.currentUrl = event?.urlAfterRedirects;
      });
    // Reactive updates on navigation
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    ).subscribe(e => {
      const url = e.urlAfterRedirects || e.url;
      this.isLoginPage = this.computeIsLoginPage(url);
      this.hideSidebar = this.isRegistrationPage(url);
      if (!this.isLoginPage && !this.hideSidebar)
        this.userInfoService.getUserInfo()
      else
        this.loading = false;
    });
    if (!this.isLoginPage && !this.hideSidebar)
      this.userInfoService.getUserInfo()
    else
      this.loading = false;

    this.userInfoService.userData$.subscribe(() => {
        if (!this.userInfoService.isAdminUser() && !this.userInfoService.hasRole(Roles.BACKOFFICE_USER)){
          this.hideSidebar = true;
          this.isBackofficeUser = false;
        }
        this.loading = false;
      }
    )

    this.breakpointObserver
      .observe(['(max-width: 768px)'])
      .subscribe(result => {
        this.isSmallScreen = result.matches;
      });

    // Start / stop SignalR connection based on authentication state
    this.authSub = this.authService.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.auditRealtime.start();
      } else {
        this.auditRealtime.stop();
      }
    });
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.auditRealtime.stop();
  }

  logout() {
    this.authService.logout({
      logoutParams: { returnTo: window.location.origin }
    });
  }

  /**
   * Login-Page-Erkennung robust gegen Query/Fragment. Beim Rückkehr aus dem
   * Auth0-Redirect-Flow enthält die URL ein `?code=…&state=…`, wodurch ein
   * naiver Vergleich `=== '/login'` fehlschlägt und die Shell fälschlich um den
   * Login-Dialog gerendert wird. (DOCU-642)
   */
  private computeIsLoginPage(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return path === '/login' || path === '/';
  }

  isRegistrationPage(path : string){
    return  path.includes('/registration')
      ||  path.includes('/registration/success')
      ||  path.includes('/user/success')
      ||  path.includes('/user/blocked')
      ||  path.includes('/user/account')
      ||  path.includes('/user/delete')
      ||  path.includes('/user/delete-success')
      ||  path.includes('/impressum')
      || path.includes('/terms')
      || path.includes('/privacy')
      || path.includes('/invite');
  }

  isProd(): boolean {
    return this.versionInfo?.env?.toLowerCase() === 'production';
  }

  switchLanguage(lang: string) {
    this.translate.use(lang);
  }

  protected readonly Roles = Roles;
}
